/* eslint-disable complexity */
import React from 'react';
import clsx from 'clsx';
import { parseISO } from 'date-fns';
import { Link, useParams } from 'react-router-dom';

import Dialog from 'carbon-react/lib/components/dialog';

import { ForecastListItem, PerformanceMetricsBusinessInsightsComparisonData } from '@futrli-sdk/predict';
import { FormattedDate, FormattedMessage } from '@futrli/localisation';
import { getOnboardingInitialRoute } from '@futrli/shared-services';
import { UISystemIcon } from '@futrli/ui-components';

import { BusinessInsightsInfoList } from './business-insights-info-list';
import { CustomersSuppliersInfoList } from './customers-suppliers-info-list';
import { EmptyBusinessInsightsInfoList } from './empty-business-insights-list';
import { Option } from '../../types';
import { PerformanceToTargetInfoList } from './performance-to-targets-info-list';
import { useCurrentOrganisation } from '../../hooks';

import * as styles from './email-preview.scss';

interface Props {
  period: Option;
  isOpen: boolean;
  handleClose: () => void;
  periodData: PerformanceMetricsBusinessInsightsComparisonData;
  performanceToTargetsPeriodData?: PerformanceMetricsBusinessInsightsComparisonData;
  selectedForecast?: ForecastListItem;
  forecastsNotExist: boolean;
  customersSuppliersInfo: {
    unpaidCashInFromInvoices: string;
    unpaidCashOutFromBills: string;
    daysNumCustomersPaying: number;
    daysNumPayingToSuppliers: number;
  } | null;
}

export const EmailPreview = ({
  isOpen,
  handleClose,
  period,
  periodData,
  performanceToTargetsPeriodData,
  selectedForecast,
  forecastsNotExist,
  customersSuppliersInfo
}: Props) => {
  const organisation = useCurrentOrganisation();
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';

  return (
    <Dialog
      open={isOpen}
      onCancel={handleClose}
      contentPadding={{
        p: 0
      }}
      className={styles.carbonCloseIcon}
    >
      <div className={styles.titleContainer}>
        <UISystemIcon iconName="white-by-sage-logo" width={120} height={55} />
      </div>
      <div className={styles.content}>
        <div className={styles.title} style={{ marginBottom: 20 }}>
          <FormattedMessage
            id="predict.insights_update_for_org_name"
            defaultMessage="Insights update for {orgName}."
            values={{ orgName: organisation?.name }}
          />
        </div>
        <div className={styles.bodyContent} style={{ marginBottom: 24 }}>
          <FormattedDate value={new Date()} weekday="long" day="numeric" month="long" year="numeric" />
          <div>
            <FormattedMessage
              id="predict.last_sync_date"
              defaultMessage="Last sync date: {lastSyncDate} at {lastSyncTime}"
              values={{
                lastSyncDate: (
                  <FormattedDate
                    value={parseISO(organisation?.datasource?.sync_status_modified || '')}
                    weekday="long"
                    day="numeric"
                    month="long"
                    year="numeric"
                  />
                ),
                lastSyncTime: (
                  <FormattedDate
                    value={parseISO(organisation?.datasource?.sync_status_modified || '')}
                    hour="2-digit"
                    minute="2-digit"
                  />
                )
              }}
            />
          </div>
          <div>
            <FormattedMessage
              id="predict.insights_period"
              defaultMessage="Insights period: {insightsPeriod} so far:"
              values={{ insightsPeriod: <span style={{ fontWeight: 700 }}>{period.name}</span> }}
            />
          </div>

          <div className={styles.boldText} style={{ marginBottom: 8, marginTop: 20 }}>
            <FormattedMessage
              id="onboarding.performance_to_prior_period"
              defaultMessage="Performance to the prior period’s total"
            />
          </div>
          <BusinessInsightsInfoList periodData={periodData} />
        </div>

        <div className={styles.boldText} style={{ marginBottom: 8, marginTop: 20 }}>
          {forecastsNotExist ? (
            <FormattedMessage
              id="onboarding.performance_to_targets_of_link"
              defaultMessage="Performance to targets of - <link>Create a forecast now</link>"
              values={{
                link: chunks => (
                  <Link
                    to={getOnboardingInitialRoute(workspaceId, organisationId)}
                    className={clsx(styles.linkText, styles.linkTextUnderlined)}
                  >
                    {chunks}
                  </Link>
                )
              }}
            />
          ) : selectedForecast ? (
            <FormattedMessage
              id="onboarding.performance_to_targets_of_name"
              defaultMessage="Performance to targets of {forecastName}."
              values={{
                forecastName: selectedForecast?.name
              }}
            />
          ) : null}
        </div>
        {forecastsNotExist ? (
          <EmptyBusinessInsightsInfoList />
        ) : performanceToTargetsPeriodData ? (
          <PerformanceToTargetInfoList periodData={performanceToTargetsPeriodData} />
        ) : null}

        {customersSuppliersInfo ? (
          <>
            <div className={styles.boldText} style={{ marginBottom: 8, marginTop: 20 }}>
              <FormattedMessage
                id="onboarding.customers_suppliers_on_last_sync"
                defaultMessage="Customers & Suppliers - on last sync"
              />
            </div>
            <CustomersSuppliersInfoList
              unpaidCashInFromInvoices={customersSuppliersInfo.unpaidCashInFromInvoices}
              unpaidCashOutFromBills={customersSuppliersInfo.unpaidCashOutFromBills}
              daysNumCustomersPaying={customersSuppliersInfo.daysNumCustomersPaying}
              daysNumPayingToSuppliers={customersSuppliersInfo.daysNumPayingToSuppliers}
            />
          </>
        ) : null}

        <div style={{ marginTop: 50, cursor: 'pointer' }} className={styles.bodyContentGreen} onClick={handleClose}>
          <FormattedMessage id="predict.change_email_preferences" defaultMessage="Change email preferences" />
        </div>
      </div>
      <div className={styles.footerContainer}>
        <div style={{ marginBottom: 30 }}>
          <UISystemIcon iconName="instagram-new" width={25} height={25} />
          <UISystemIcon iconName="facebook-icon" width={25} height={25} />
          <UISystemIcon iconName="linkedin-new" width={25} height={25} />
          <UISystemIcon iconName="twitter-icon" width={25} height={25} />
          <UISystemIcon iconName="youtube-icon" width={25} height={25} />
        </div>
        <div className={styles.dividerBar} />
        <div className={styles.futrliMessage} style={{ marginTop: 16 }}>
          <FormattedMessage
            id="common.futrli_sage_info"
            defaultMessage="Sage (UK) Limited C23 5 & 6 Cobalt Park Way, Cobalt Business Park, Newcastle-upon-Tyne, Tyne & Wear, NE28 9EJ, United Kingdom VAT number: GB121084165"
          />
        </div>
      </div>
    </Dialog>
  );
};
